import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { useGetDeployedAgentQuery, useRenameDeployedAgentMutation } from "@/store/admin_api";
import { apiErrorMessage } from "@/util/apiError";
import AgentConfigPanel from "./components/AgentConfigPanel";

export default function ConfigureDeployedAgent() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading, isError } = useGetDeployedAgentQuery(agentId!);
  const [renameAgent, { isLoading: isSavingName }] = useRenameDeployedAgentMutation();
  const [name, setName] = useState("");

  const listRoute = "/platform-admin/ai-agents/deployed";
  const isNameDirty = agent != null && name.trim() !== "" && name.trim() !== agent.name;

  const handleRenameAgent = async () => {
    if (!agent || !isNameDirty) {
      return;
    }
    try {
      await renameAgent({ agentId: agent.id, name: name.trim() }).unwrap();
      toast.success("Agent renamed");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to rename agent"));
    }
  };

  useEffect(() => {
    if (agent) {
      setName(agent.name ?? "");
    }
  }, [agent]);

  if (isLoading) {
    return (
      <div className="space-y-6 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !agent) {
    return (
      <div className="space-y-6 p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(listRoute)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Agent not found</h1>
            <p className="text-muted-foreground">This deployed agent does not exist or was removed.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(listRoute)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">{agent.name}</h1>
            <Badge variant={agent.status === "ACTIVE" ? "default" : "secondary"}>{agent.status}</Badge>
          </div>
          <p className="text-muted-foreground">
            {agent.organisation?.name ?? "No organisation"} · {agent.template?.name ?? "No template"} · {agent.orchestrator}
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-1.5 shrink-0"
          onClick={() =>
            navigate(`/platform-admin/ai-agents/test?organisation=${agent.organisation?.id ?? ""}&agent=${agent.id}`)
          }
        >
          <PhoneCall className="h-4 w-4" /> Test call
        </Button>
      </div>

      <div className="rounded-lg border p-4 space-y-1.5 max-w-md">
        <Label htmlFor="agent-name">Agent name</Label>
        <div className="flex gap-2">
          <Input
            id="agent-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Agent name"
          />
          <Button onClick={handleRenameAgent} disabled={!isNameDirty || isSavingName}>
            {isSavingName && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          What the organisation sees for this agent. Org members can rename it too.
        </p>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <AgentConfigPanel
          key={agent.id}
          agentId={agent.id}
          orchestrator={agent.orchestrator}
          voiceProvider={agent.voiceProvider ?? null}
          voiceId={agent.voiceId ?? null}
          llmProvider={agent.llmProvider ?? null}
          llmModel={agent.llmModel ?? null}
          customConfig={agent.customConfig as Record<string, any> | null | undefined}
          promptVersionId={agent.promptVersionId ?? null}
          promptVersionLabel={agent.promptVersionLabel ?? null}
          tools={agent.effectiveTools ?? agent.tools ?? null}
          declaredOutcomes={agent.effectiveDeclaredOutcomes ?? agent.declaredOutcomes ?? null}
          skills={agent.skills ?? null}
          templateSkills={agent.template?.defaultSkills ?? null}
          greeting={agent.greeting ?? null}
          closingMessage={agent.closingMessage ?? null}
        />
      </div>
    </div>
  );
}
